import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { snackbarAtom } from '@/atoms/snackbar';
import { jotaiStore } from '@/atoms/store';
import { snackbarColor } from '@/enums';
import { ConfirmationModal } from './ConfirmationModal';
import { type ConfirmEntry, type CustomEntry, type ModalEntry, modalStackAtom, removeEntry, setPending } from './stack';

const focusElement = (el: Element | null) => {
    if (!el || !(el instanceof HTMLElement)) return;
    if (!document.contains(el)) return;
    try {
        el.focus({ preventScroll: true });
    } catch {
        /* ignore */
    }
};

export const ModalHost = () => {
    const stack = useAtomValue(modalStackAtom);
    const location = useLocation();
    const { t } = useTranslation();
    const prevStackRef = useRef<ModalEntry[]>([]);

    useEffect(() => {
        const prev = prevStackRef.current;
        const currentIds = new Set(stack.map(e => e.id));
        const removed = prev.filter(e => !currentIds.has(e.id));
        if (removed.length > 0) {
            const target = removed[removed.length - 1]?.triggerElement ?? null;
            queueMicrotask(() => focusElement(target));
        }
        prevStackRef.current = stack;
    }, [stack]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fires on pathname change; stack is read live from the store inside the effect.
    useEffect(() => {
        const cur = jotaiStore.get(modalStackAtom);
        if (cur.length === 0) return;
        for (const entry of cur) {
            if (entry.dismissOnNavigate === false) continue;
            if (entry.kind === 'confirm') entry.resolve(false);
            else entry.resolve(undefined);
            removeEntry(entry.id);
        }
    }, [location.pathname]);

    return (
        <>
            {stack.map(entry =>
                entry.kind === 'confirm' ? (
                    <ConfirmHostEntry key={entry.id} entry={entry} fallbackError={t('helpers.not-valid')} />
                ) : (
                    <CustomHostEntry key={entry.id} entry={entry} />
                ),
            )}
        </>
    );
};

const ConfirmHostEntry = ({ entry, fallbackError }: { entry: ConfirmEntry; fallbackError: string }) => {
    const close = (value: boolean) => {
        entry.resolve(value);
        removeEntry(entry.id);
    };

    const handleConfirm = async () => {
        const onConfirm = entry.options.onConfirm;
        if (!onConfirm) {
            close(true);
            return;
        }
        const result = onConfirm();
        if (!(result instanceof Promise)) {
            close(true);
            return;
        }
        setPending(entry.id, true);
        try {
            await result;
            close(true);
        } catch (err) {
            setPending(entry.id, false);
            const message = err instanceof Error && err.message ? err.message : fallbackError;
            jotaiStore.set(snackbarAtom, {
                open: true,
                message,
                severity: snackbarColor.error,
                vertical: 'bottom',
                horizontal: 'center',
            });
        }
    };

    return (
        <ConfirmationModal
            open
            title={entry.options.title}
            message={entry.options.message}
            confirmLabel={entry.options.confirmLabel}
            cancelLabel={entry.options.cancelLabel}
            severity={entry.options.severity}
            pending={entry.pending}
            onConfirm={handleConfirm}
            onCancel={() => {
                if (entry.pending) return;
                close(false);
            }}
        />
    );
};

const CustomHostEntry = ({ entry }: { entry: CustomEntry }) => {
    const Component = entry.Component;
    const close = () => {
        entry.resolve(undefined);
        removeEntry(entry.id);
    };
    return (
        <Component
            {...entry.payload}
            open
            onClose={() => {
                if (entry.pending) return;
                close();
            }}
            resolve={(value: unknown) => {
                entry.resolve(value);
                removeEntry(entry.id);
            }}
            setPending={(pending: boolean) => setPending(entry.id, pending)}
        />
    );
};
